import { ChangeEvent, FormEvent, useContext, useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup } from 'react-bootstrap';
import TemplateEditor from '../../ui/TemplateEditor/TemplateEditor';
import toast from 'react-hot-toast';
import { configuration } from 'src/configuration';
import AppContext from '../../../store/appContext';
import useEmailTemplates from '../../../hooks/useEmailTemplates';
import { sortTemplates } from '../../../utils/sortTemplates';
import { getApiErrorMessage, getApiErrorStatus } from '../../../utils/apiError';
import { hasRole, ROLES } from '../../../constants/roles';
import { SYSTEM_TEMPLATES_LABEL, CUSTOM_REPLIES_LABEL } from '../../ui/TemplatePicker/TemplatePicker.constants';

/** Sentinel `<Form.Select>` value used to enter "create a new template" mode. */
const NEW_TEMPLATE_VALUE = 'new';

interface IEditableTemplateFields {
    name: string;
    to: string | null;
    cc: string | null;
    bcc: string | null;
    subject: string;
    message: string;
    addMessage: string;
    priority: number;
    isCustom: boolean;
}

const EMPTY_FORM: IEditableTemplateFields = {
    name: '',
    to: null,
    cc: null,
    bcc: null,
    subject: '',
    message: '',
    addMessage: '',
    priority: 0,
    isCustom: true,
};

const DELETE_CONFLICT_MESSAGE = 'Šablonu nelze smazat — je používána v jiné části aplikace.';
const CREATE_ERROR_FALLBACK = 'Nepodařilo se vytvořit šablonu.';
const UPDATE_ERROR_FALLBACK = 'Nepodařilo se uložit šablonu.';
const DELETE_ERROR_FALLBACK = 'Nepodařilo se smazat šablonu.';
const VALIDATION_ERROR = 'Vyplňte prosím název a zprávu šablony.';

export default function AdminTemplates() {
    const { whoAmI } = useContext(AppContext);
    const { templates, isLoading, error, createTemplate, updateTemplate, deleteTemplate } = useEmailTemplates();

    const [selectedId, setSelectedId] = useState<number | typeof NEW_TEMPLATE_VALUE | ''>('');
    const [form, setForm] = useState<IEditableTemplateFields>(EMPTY_FORM);

    const sortedTemplates = sortTemplates(templates);
    const selectedTemplate =
        typeof selectedId === 'number' ? templates.find((template) => template.id === selectedId) ?? null : null;
    const isCreateMode = selectedId === NEW_TEMPLATE_VALUE;

    useEffect(() => {
        if (selectedTemplate) {
            setForm({
                name: selectedTemplate.name,
                to: selectedTemplate.to,
                cc: selectedTemplate.cc,
                bcc: selectedTemplate.bcc,
                subject: selectedTemplate.subject,
                message: selectedTemplate.message,
                addMessage: selectedTemplate.addMessage,
                priority: selectedTemplate.priority,
                isCustom: selectedTemplate.isCustom,
            });
        } else if (isCreateMode) {
            setForm(EMPTY_FORM);
        }
    }, [selectedTemplate, isCreateMode]);

    const onSelectionChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        if (value === '') {
            setSelectedId('');
        } else if (value === NEW_TEMPLATE_VALUE) {
            setSelectedId(NEW_TEMPLATE_VALUE);
        } else {
            setSelectedId(Number(value));
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handlePriorityChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, priority: value === '' ? 0 : Number(value) }));
    };

    const handleEditorChange = (field: 'message' | 'addMessage', content: string) => {
        setForm((prev) => ({ ...prev, [field]: content }));
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        if (!form.name.trim() || !form.message.trim()) {
            toast.error(VALIDATION_ERROR);
            return;
        }

        try {
            if (isCreateMode) {
                const created = await createTemplate({
                    name: form.name,
                    to: form.to || undefined,
                    cc: form.cc || undefined,
                    bcc: form.bcc || undefined,
                    subject: form.subject || undefined,
                    message: form.message,
                    addMessage: form.addMessage || undefined,
                    priority: form.priority,
                    isCustom: form.isCustom,
                });
                setSelectedId(created.id);
                toast.success('Šablona byla vytvořena.');
            } else if (selectedTemplate) {
                await updateTemplate(selectedTemplate.id, {
                    to: form.to,
                    cc: form.cc,
                    bcc: form.bcc,
                    subject: form.subject,
                    message: form.message,
                    addMessage: form.addMessage,
                    name: form.name,
                    priority: form.priority,
                });
                toast.success('Šablona byla uložena.');
            }
        } catch (err) {
            const fallback = isCreateMode ? CREATE_ERROR_FALLBACK : UPDATE_ERROR_FALLBACK;
            toast.error(await getApiErrorMessage(err, fallback));
        }
    };

    const handleDelete = async () => {
        if (!selectedTemplate) {
            return;
        }

        if (!window.confirm('Opravdu chcete tuto šablonu smazat?')) {
            return;
        }

        try {
            await deleteTemplate(selectedTemplate.id);
            toast.success('Šablona byla smazána.');
            setSelectedId('');
        } catch (err) {
            if (getApiErrorStatus(err) === 409) {
                toast.error(DELETE_CONFLICT_MESSAGE);
            } else {
                toast.error(await getApiErrorMessage(err, DELETE_ERROR_FALLBACK));
            }
        }
    };

    // `whoAmI` is loaded asynchronously by the surrounding admin layout
    // (`views/admin/index.tsx`) — avoid flashing an editable form before we
    // know whether the current user is allowed to edit templates.
    if (!whoAmI) {
        return null;
    }

    const canEdit = hasRole(whoAmI, ROLES.CHIEF_OPERATOR, ROLES.ADMIN);
    const showForm = !!selectedTemplate || isCreateMode;

    return (
        <div className="mb-5">
            <h3 className="pb-3">Nastavení šablon odpovědí</h3>
            {!canEdit && (
                <Alert variant="info">
                    Šablony odpovědí může upravovat pouze vedoucí operátor nebo administrátor. Zobrazený náhled je pouze pro čtení.
                </Alert>
            )}
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-6 d-flex gap-2 align-items-start">
                    <Form.Select value={selectedId} onChange={onSelectionChange} disabled={isLoading}>
                        <option value="">-- Vyberte automatickou odpověď --</option>
                        {[{ isCustom: false, label: SYSTEM_TEMPLATES_LABEL }, { isCustom: true, label: CUSTOM_REPLIES_LABEL }].map((group) => (
                            <optgroup key={group.label} label={group.label}>
                                {sortedTemplates.filter((template) => !!template.isCustom === group.isCustom).map((template) => (
                                    <option key={template.id} value={template.id}>
                                        {`[${template.priority}] ${template.name}`}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                        {isCreateMode && <option value={NEW_TEMPLATE_VALUE}>-- Nová šablona --</option>}
                    </Form.Select>
                    {canEdit && (
                        <Button
                            type="button"
                            variant="outline-primary"
                            className="text-nowrap"
                            onClick={() => setSelectedId(NEW_TEMPLATE_VALUE)}
                        >
                            Nová šablona
                        </Button>
                    )}
                </Form.Group>
                {isCreateMode && (
                    <Form.Group className="mt-3" controlId="template-type">
                        <Form.Label>Typ šablony</Form.Label>
                        <Form.Select
                            value={form.isCustom ? 'custom' : 'system'}
                            onChange={(event) => setForm((prev) => ({ ...prev, isCustom: event.target.value === 'custom' }))}
                            disabled={!canEdit}
                        >
                            <option value="custom">Vlastní odpověď</option>
                            <option value="system">Systémová šablona</option>
                        </Form.Select>
                    </Form.Group>
                )}
                {showForm && (
                    <>
                        <InputGroup className="mt-5 mb-3">
                            <InputGroup.Text id="basic-addon-name">Název:</InputGroup.Text>
                            <Form.Control
                                type="text"
                                name="name"
                                aria-label="Název šablony"
                                placeholder="Název šablony"
                                value={form.name}
                                onChange={handleChange}
                                disabled={!canEdit}
                            />
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="basic-addon-priority">Priorita:</InputGroup.Text>
                            <Form.Control
                                type="number"
                                name="priority"
                                aria-label="Priorita"
                                min={0}
                                step={1}
                                value={form.priority}
                                onChange={handlePriorityChange}
                                disabled={!canEdit}
                            />
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="basic-addon1">Komu:</InputGroup.Text>
                            <Form.Control
                                type="email"
                                name="to"
                                placeholder="uzivatel@email.cz"
                                value={form.to ?? ''}
                                onChange={handleChange}
                                disabled={!canEdit}
                            />
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="basic-addon1">Kopie:</InputGroup.Text>
                            <Form.Control
                                type="email"
                                name="cc"
                                placeholder="uzivatel@email.cz"
                                value={form.cc ?? ''}
                                onChange={handleChange}
                                disabled={!canEdit}
                            />
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="basic-addon1">Skrytá kopie:</InputGroup.Text>
                            <Form.Control
                                type="email"
                                name="bcc"
                                placeholder="uzivatel@email.cz"
                                value={form.bcc ?? ''}
                                onChange={handleChange}
                                disabled={!canEdit}
                            />
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="basic-addon1">Předmět:</InputGroup.Text>
                            <Form.Control
                                type="text"
                                name="subject"
                                placeholder="Text"
                                value={form.subject}
                                onChange={handleChange}
                                disabled={!canEdit}
                            />
                        </InputGroup>
                        <Form.Label column sm="2">
                            <b>Zpráva:</b>
                        </Form.Label>
                        <TemplateEditor
                            key={`${selectedId}-message`}
                            id="message"
                            value={form.message}
                            onChange={(content) => handleEditorChange('message', content)}
                            readOnly={!canEdit}
                        />
                        <Form.Label column sm="2" className="mt-3">
                            <b>Dodatečná zpráva:</b>
                        </Form.Label>
                        <TemplateEditor
                            key={`${selectedId}-addMessage`}
                            id="addMessage"
                            value={form.addMessage}
                            onChange={(content) => handleEditorChange('addMessage', content)}
                            readOnly={!canEdit}
                        />
                        {canEdit && (
                            <div className="clearfix">
                                {!isCreateMode && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="danger"
                                        className="mt-3 float-start"
                                        onClick={handleDelete}
                                    >
                                        Smazat
                                    </Button>
                                )}
                                <Button type="submit" size="sm" variant="success" className="mt-3 float-end">
                                    Uložit
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Form>
        </div>
    );
}
